import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'

import type { MethodAmount, ReportPaymentMethod } from '@/lib/api/report-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { DateRangePresets } from '@/components/common/filters/date-range-presets'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useCashFlow } from '@/hooks/use-report-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Trước đây cả ba nằm trong `useState` nên không vào URL — màn báo cáo mà không
 * gửi được link kèm kỳ đang xem thì gần như vô dụng khi trao đổi (R9).
 */
const FILTERS = defineListFilters({
    date_from: dateFilter({ label: m.cashflow_filter_from }),
    date_to: dateFilter({ label: m.cashflow_filter_to }),
    payment_method: selectFilter<'all' | ReportPaymentMethod>({
        label: m.expense_payment_method,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.expense_filter_all_methods() },
            { value: 'cash', label: m.expense_payment_method_cash() },
            {
                value: 'bank_transfer',
                label: m.expense_payment_method_bank_transfer(),
            },
        ],
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/cash-flow',
)({
    component: CashFlowPage,
    validateSearch: FILTERS.validateSearch,
})

function methodLabel(method: string): string {
    switch (method) {
        case 'cash':
            return m.cashflow_method_cash()
        case 'bank_transfer':
            return m.cashflow_method_bank_transfer()
        case 'card':
            return m.cashflow_method_card()
        // Money the carrier collected on delivery: real revenue, but a
        // receivable from the carrier until their remittance clears — so it
        // must not read as an unlabelled "other".
        case 'cod_via_carrier':
            return m.cashflow_method_cod_via_carrier()
        default:
            return m.cashflow_method_other()
    }
}

function sum(rows: MethodAmount[]): number {
    return rows.reduce((s, r) => s + r.amount, 0)
}

function MethodTable({ title, rows }: { title: string; rows: MethodAmount[] }) {
    return (
        <div className="space-y-2">
            <h2 className="font-semibold">{title}</h2>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.cashflow_method()}</TableHead>
                        <TableHead className="text-right">
                            {m.cashflow_amount()}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={2}
                                className="text-center text-muted-foreground py-4"
                            >
                                {m.cashflow_empty()}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((r) => (
                        <TableRow key={r.method}>
                            <TableCell>{methodLabel(r.method)}</TableCell>
                            <TableCell className="text-right font-mono">
                                {formatVnd(r.amount)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="border-t-2 font-semibold">
                        <TableCell>{m.cashflow_total()}</TableCell>
                        <TableCell className="text-right font-mono">
                            {formatVnd(sum(rows))}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    )
}

function StatCard({ label, value }: { label: string; value: number }) {
    return (
        <div className="rounded border p-3">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div
                className={`font-mono text-lg font-semibold ${
                    value < 0 ? 'text-red-600' : ''
                }`}
            >
                {formatVnd(value)}
            </div>
        </div>
    )
}

function CashFlowPage() {
    const navigate = useNavigate({ from: '/accounting/cash-flow' })
    const { values, barProps } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data } = useCashFlow({
        date_from: values.date_from || undefined,
        date_to: values.date_to || undefined,
        payment_method:
            values.payment_method === 'all' ? undefined : values.payment_method,
    })

    return (
        <div className="space-y-4 page-pad">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <div>
                    <h1 className="text-xl font-semibold">
                        {m.cashflow_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.cashflow_subtitle()}
                    </p>
                </div>
            </div>

            <DateRangePresets
                onPick={(range) =>
                    barProps.onApply({
                        ...values,
                        date_from: range.from,
                        date_to: range.to,
                    })
                }
            />
            <ListFilterBar {...barProps} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <MethodTable
                    title={m.cashflow_inflow()}
                    rows={data?.inflow_by_method ?? []}
                />
                <MethodTable
                    title={m.cashflow_outflow()}
                    rows={data?.outflow_by_method ?? []}
                />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatCard label={m.cashflow_net()} value={data?.net ?? 0} />
                <StatCard
                    label={m.cashflow_cash_net()}
                    value={data?.cash_net ?? 0}
                />
                <StatCard
                    label={m.cashflow_bank_net()}
                    value={data?.bank_net ?? 0}
                />
            </div>

            <div className="rounded border p-3 space-y-2">
                <h2 className="font-semibold">{m.cashflow_recon_title()}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <StatCard
                        label={m.cashflow_bank_actual()}
                        value={data?.bank_actual ?? 0}
                    />
                    <StatCard
                        label={m.cashflow_bank_recorded()}
                        value={data?.bank_recorded ?? 0}
                    />
                    <StatCard
                        label={m.cashflow_bank_diff()}
                        value={data?.bank_difference ?? 0}
                    />
                </div>
                {(data?.bank_difference ?? 0) > 0 && (
                    <Link
                        to="/accounting/reconciliation"
                        className="inline-block text-sm text-primary underline"
                    >
                        {m.cashflow_bank_diff_link()}
                    </Link>
                )}
            </div>

            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm space-y-1">
                <div className="font-semibold">{m.cashflow_caveat_title()}</div>
                <ul className="list-disc pl-5 text-muted-foreground">
                    <li>{m.cashflow_caveat_cancelled()}</li>
                    <li>{m.cashflow_caveat_outflow()}</li>
                    <li>{m.cashflow_caveat_bankdiff()}</li>
                </ul>
            </div>
        </div>
    )
}
